import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { Observable } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { QuestionnaireType } from 'src/API';
import { Assessment } from 'src/app/core/api/assessment.service';
import { isWithinTimeFrameInDays } from 'src/app/core/api/date.util';
import {
  QuestionnaireRequest,
  QuestionnaireRequestService
} from 'src/app/core/api/questionnaire-request.service';
import { AppStateService } from 'src/app/core/services/app-state.service';
import { StaffPermissionsService } from 'src/app/core/services/staff-permissions.service';
import { SendQuestionnaireDialogComponent } from 'src/app/shared/send-questionnaire-dialog/send-questionnaire-dialog.component';

@Component({
  selector: 'csi-osdi6-link-suggestion',
  templateUrl: './osdi6-link-suggestion.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class Osdi6LinkSuggestionComponent implements OnInit, OnDestroy {
  @Input() isLocked: boolean;
  @Input() formGroup: FormGroup;
  @Input() saveFn: () => Observable<Assessment>;

  private _assessment: Assessment = null;
  @Input() set assessment(value: Assessment) {
    this._assessment = value;
    this._evaluateSuggestions();
    this.changeRef.markForCheck();
  }
  get assessment(): Assessment {
    return this._assessment;
  }

  @Output() questionnaireLinked = new EventEmitter<QuestionnaireRequest>();

  public suggestedOsdi6: QuestionnaireRequest = null;
  public suggestedDewsIII: QuestionnaireRequest = null;

  // Precomputed beside the suggestions above so the template binds a plain property
  // instead of calling getSuggestionText() on every change detection cycle.
  public suggestedOsdi6Text = '';
  public suggestedDewsIIIText = '';
  public osdi6Dismissed = false;
  public dewsIIIDismissed = false;
  sendDismissed = false;
  sendDismissing = false;
  isApplying = false;

  private questionnaireRequests = [];
  public questionnaireRequestsByType: {
    [key in Exclude<QuestionnaireType, QuestionnaireType.CUSTOM>]: QuestionnaireRequest[];
  } & { [key: string]: QuestionnaireRequest[] } = {
    [QuestionnaireType.FITZPATRICK]: [],
    [QuestionnaireType.CDERFS]: [],
    [QuestionnaireType.DERFS]: [],
    [QuestionnaireType.DEQ]: [],
    [QuestionnaireType.SPEED]: [],
    [QuestionnaireType.SPEEDII]: [],
    [QuestionnaireType.OSDI]: [],
    [QuestionnaireType.OSDIAndSPEED]: [],
    [QuestionnaireType.OSDI6]: [],
    [QuestionnaireType.MEDICATION]: [],
    [QuestionnaireType.DEWSIII_Q]: []
  };

  constructor(
    private appStateService: AppStateService,
    private questionnaireRequestService: QuestionnaireRequestService,
    public staffPermissionsService: StaffPermissionsService,
    private datePipe: DatePipe,
    private changeRef: ChangeDetectorRef,
    private matDialog: MatDialog
  ) {}

  ngOnInit() {
    this.formGroup.controls.assessmentMethod.valueChanges
      .pipe(startWith(this.formGroup.controls.assessmentMethod.value), untilDestroyed(this))
      .subscribe(() => {
        this._loadAndEvaluate();
      });
  }

  private _loadAndEvaluate() {
    const currentPatient = this.appStateService.getCurrentPatient();
    if (currentPatient && currentPatient.questionnaireRequests) {
      this.questionnaireRequests = currentPatient.questionnaireRequests.items;
      this._groupQuestionnaireRequestsByType();
      this._evaluateSuggestions();
      this.changeRef.markForCheck();
    }
  }

  private _evaluateSuggestions() {
    const assessmentDate = this._getAssessmentDate();

    const lastOsdi6 = this.questionnaireRequestService.getLastCompletedQuestionnaire(
      this.questionnaireRequestsByType[QuestionnaireType.OSDI6]
    );
    if (
      lastOsdi6 &&
      !this._hasLinkedByType(QuestionnaireType.OSDI6) &&
      isWithinTimeFrameInDays(assessmentDate, new Date(lastOsdi6.completedAt), 4 * 30)
    ) {
      this.suggestedOsdi6 = { ...lastOsdi6 };
      this.suggestedOsdi6Text = this.getSuggestionText(this.suggestedOsdi6);
    }

    const lastDewsIII = this.questionnaireRequestService.getLastCompletedQuestionnaire(
      this.questionnaireRequestsByType[QuestionnaireType.DEWSIII_Q]
    );
    if (
      lastDewsIII &&
      !this._hasLinkedByType(QuestionnaireType.DEWSIII_Q) &&
      isWithinTimeFrameInDays(assessmentDate, new Date(lastDewsIII.completedAt), 4 * 30)
    ) {
      this.suggestedDewsIII = { ...lastDewsIII };
      this.suggestedDewsIIIText = this.getSuggestionText(this.suggestedDewsIII);
    }
  }

  private _getAssessmentDate(): Date {
    const date =
      this._assessment && this._assessment.createdAt
        ? new Date(this._assessment.createdAt)
        : new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }

  filterQuestionnairesByTimeFrameFromCompletion(
    questionnairesToFilter: QuestionnaireRequest[],
    timeFrameInDays: number
  ) {
    const assessmentDate = this._getAssessmentDate();
    return questionnairesToFilter.filter(questionnaire => {
      if (!questionnaire.completedAt) return false;
      const questionnaireDate = new Date(questionnaire.completedAt);
      questionnaireDate.setHours(0, 0, 0, 0);
      return isWithinTimeFrameInDays(assessmentDate, questionnaireDate, timeFrameInDays);
    });
  }

  private _groupQuestionnaireRequestsByType() {
    Object.keys(this.questionnaireRequestsByType).forEach(key => {
      this.questionnaireRequestsByType[key] = [];
    });
    this.questionnaireRequests.forEach(questionnaireRequest => {
      if (questionnaireRequest.type === QuestionnaireType.CUSTOM) {
        if (!questionnaireRequest.config) {
          return;
        }
        const questionnaireType =
          (questionnaireRequest.config as any) instanceof Object
            ? questionnaireRequest.config['schema']['abbreviation']
            : JSON.parse(questionnaireRequest.config).schema.abbreviation;

        if (!this.questionnaireRequestsByType[questionnaireType]) {
          this.questionnaireRequestsByType[questionnaireType] = [];
        }
        this.questionnaireRequestsByType[questionnaireType].push(questionnaireRequest);
      } else {
        this.questionnaireRequestsByType[questionnaireRequest.type as QuestionnaireType].push(
          questionnaireRequest
        );
      }
    });
  }

  ngOnDestroy() {}

  get shouldShowSend(): boolean {
    if (this.sendDismissed || this.suggestedOsdi6 !== null || this.suggestedDewsIII !== null) {
      return false;
    }
    if (!this.appStateService.getCurrentPatient()) {
      return false;
    }
    if (this._hasLinkedOsdi6()) {
      return false;
    }
    return true;
  }

  getSuggestionText(questionnaire: QuestionnaireRequest): string {
    const date = this.datePipe.transform(questionnaire.completedAt, 'MMM dd, yyyy');
    const label =
      questionnaire.type === QuestionnaireType.OSDI6 ? 'OSDI 6' : 'DEWS III Questionnaire';
    return 'Link to ' + label + ' completed on ' + date;
  }

  dismissOsdi6() {
    this.osdi6Dismissed = true;
    this.changeRef.markForCheck();
  }

  dismissDewsIII() {
    this.dewsIIIDismissed = true;
    this.changeRef.markForCheck();
  }

  dismissSend() {
    this.sendDismissed = true;
    this.changeRef.markForCheck();
  }

  openSendDialog() {
    const currentPatient = this.appStateService.getCurrentPatient();
    if (this.isLocked || !currentPatient) {
      return;
    }
    SendQuestionnaireDialogComponent.open(
      this.matDialog,
      QuestionnaireType.OSDI6,
      currentPatient,
      {},
      null,
      true
    )
      .afterClosed()
      .pipe(untilDestroyed(this))
      .subscribe(result => {
        if (result) {
          this.sendDismissing = true;
          this.changeRef.markForCheck();
        }
      });
  }

  applyOsdi6() {
    this._doApply(this.suggestedOsdi6, () => {
      this.osdi6Dismissed = true;
    });
  }

  applyDewsIII() {
    this._doApply(this.suggestedDewsIII, () => {
      this.dewsIIIDismissed = true;
    });
  }

  private _doApply(questionnaire: QuestionnaireRequest, onSuccess: () => void) {
    if (!this.staffPermissionsService.permissions.canLinkQuestionnaire) {
      return;
    }
    if (this.isLocked || !questionnaire || this.isApplying) {
      return;
    }
    if (!this._assessment && !this.saveFn) {
      return;
    }

    this.isApplying = true;
    this.changeRef.markForCheck();

    const doLink = (assessment: Assessment) => {
      this.questionnaireRequestService
        .updateQuestionnaireRequest(
          {
            id: questionnaire.id,
            questionnaireRequestAssessmentId: assessment.id
          },
          false
        )
        .subscribe(
          () => {
            const currentPatient = this.appStateService.getCurrentPatient();
            if (
              currentPatient &&
              currentPatient.questionnaireRequests &&
              currentPatient.questionnaireRequests.items
            ) {
              const items = currentPatient.questionnaireRequests.items as QuestionnaireRequest[];
              items.forEach(q => {
                if (q.id === questionnaire.id) {
                  (q as any).assessment = assessment;
                }
              });
            }

            this.questionnaireLinked.emit(questionnaire);
            onSuccess();
            this.isApplying = false;
            this.changeRef.markForCheck();
          },
          () => {
            this.isApplying = false;
            this.changeRef.markForCheck();
          }
        );
    };

    if (!this._assessment) {
      this.saveFn()
        .pipe(untilDestroyed(this))
        .subscribe(
          savedAssessment => {
            this._assessment = savedAssessment;
            doLink(savedAssessment);
          },
          () => {
            this.isApplying = false;
            this.changeRef.markForCheck();
          }
        );
    } else {
      doLink(this._assessment);
    }
  }

  private _hasLinkedByType(type: QuestionnaireType): boolean {
    if (!this._assessment || !this._assessment.id) {
      return false;
    }
    const currentPatient = this.appStateService.getCurrentPatient();
    if (
      !currentPatient ||
      !currentPatient.questionnaireRequests ||
      !currentPatient.questionnaireRequests.items
    ) {
      return false;
    }
    const items = currentPatient.questionnaireRequests.items as QuestionnaireRequest[];
    return items.some(
      q => q && q.type === type && q.assessment && q.assessment.id === this._assessment.id
    );
  }

  private _hasLinkedOsdi6(): boolean {
    return (
      this._hasLinkedByType(QuestionnaireType.OSDI6) ||
      this._hasLinkedByType(QuestionnaireType.DEWSIII_Q)
    );
  }
}
